import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Chip,
  useTheme,
  IconButton,
  Tooltip,
  Alert,
  Button,
  Popover,
  MenuItem,
} from '@mui/material';
import {
  Copy,
  Check,
  Pencil,
  MessageSquare,
  Rocket,
  Forward,
  History,
} from 'lucide-mui';
import type { AppDispatch } from '../../../../../store';
import { SpeakerType, CompactSegment } from '../../../../../types';
import { RootState } from '../../../../../reducers';
import { onClickTimestamp } from '../../../../../lib/commonUtils';
import { formatSegmentTime } from '../../../../../lib/transcriptionHelpers';
import useLimits from '../../../../plan/hooks/limits';
import CommentDialog from '../../Comment';
import SpeakerSelect from './speakerSelect';
import { shareThunk, updateSegmentText } from '../../../selectedVideoSlice';
import EditDialog from './EditDialog';
import HighlightedText from './HighlightedText';
import EditHistoryDialog from './EditHistoryDialog';

interface SegmentProps {
  segment: CompactSegment;
  isCurrentSegment: boolean;
  shouldBlur: boolean;
  onEditStart: (segmentId: number) => void;
  onEditEnd: () => void;
  searchTerm?: string;
  editingSegmentId: number | null;
}

const SegmentPanel: React.FC<SegmentProps> = ({
  segment,
  isCurrentSegment,
  shouldBlur,
  onEditStart,
  onEditEnd,
  searchTerm,
  editingSegmentId,
}) => {
  const { text, speaker: speakerId, start, end } = segment;
  const { selectedVideo } = useSelector((state: RootState) => state);
  const { speakers } = selectedVideo?.file?.textData || {};
  let speaker: SpeakerType = {
    name: '',
    picture: '',
    userId: '',
  };

  if (speakerId) {
    speaker = speakers?.[speakerId] || speaker;
  }

  const theme = useTheme();
  const guardLimits = useLimits();
  const [isHovered, setIsHovered] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [showUpgradeMessage, setShowUpgradeMessage] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedNote, setSelectedNote] = useState<
    { text: string; timestamp: number } | undefined
  >(undefined);
  const dispatch = useDispatch<AppDispatch>();
  const [isClipCopied, setIsClipCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  const notes = useSelector(
    (state: RootState) => state.selectedVideo.file?.textData?.notes || []
  );

  const getNotesInSegment = () => {
    return notes.filter(
      (note) => note.timestamp >= start && note.timestamp <= end
    );
  };

  const hasNoteAtTimestamp = () => {
    return getNotesInSegment().length > 0;
  };

  const scrambleText = (input: string) => {
    return input
      .split('')
      .sort(() => Math.random() - 0.5)
      .join('');
  };

  const formatTimestamp = (timestamp: string) => {
    const cleanTimestamp = timestamp.replace(/[[\]()\s]/g, '');
    const timeParts = cleanTimestamp.split(':').map(Number);

    if (timeParts.length === 2) {
      const [minutes, seconds] = timeParts;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
        2,
        '0'
      )}`;
    }
    if (timeParts.length === 3) {
      const [hours, minutes, seconds] = timeParts;
      return hours === 0
        ? `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
            2,
            '0'
          )}`
        : `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
            2,
            '0'
          )}:${String(seconds).padStart(2, '0')}`;
    }

    return timestamp;
  };

  const handleClick = () => {
    if (!shouldBlur) {
      onClickTimestamp(start);
    }
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 2000);
        })
        .catch((err) => {
          console.error('Failed to copy text: ', err);
        });
    }
  };

  const handleCommentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedNote(undefined);
    setCommentDialogOpen(true);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    onEditStart(segment.id);
  };

  const handleUpgrade = () => {
    guardLimits('fullTranscript', () => {
      // Handle upgrade logic here, if needed.
    });
  };

  const handleClipShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const { start: startTime, end: endTime } = segment;

    // Get existing shareId or create new one
    const shareId = selectedVideo.file.share?.shareId;
    if (shareId) {
      const basePath = window.location.pathname.replace(/\/$/, ''); // Remove trailing slash if present
      const shareUrl = `${window.location.origin}${basePath}/#/shared/${shareId}?start=${startTime}&end=${endTime}`;
      await navigator.clipboard.writeText(shareUrl);
      setIsClipCopied(true);
      setTimeout(() => setIsClipCopied(false), 2000);
      // Open in new window after a short delay
      setTimeout(() => {
        window.open(shareUrl, '_blank', 'noopener');
      }, 500);
    } else {
      // Only dispatch if we don't have a shareId
      await dispatch(
        shareThunk({
          fileId: selectedVideo.file._id,
          copyUrlToClipboard: false,
        })
      );
      const newShareId = selectedVideo.file.share?.shareId;
      if (newShareId) {
        const basePath = window.location.pathname.replace(/\/$/, ''); // Remove trailing slash if present
        const shareUrl = `${window.location.origin}${basePath}/#/shared/${newShareId}?start=${startTime}&end=${endTime}`;
        await navigator.clipboard.writeText(shareUrl);
        setIsClipCopied(true);
        setTimeout(() => setIsClipCopied(false), 2000);
        // Open in new window after a short delay
        setTimeout(() => {
          window.open(shareUrl, '_blank', 'noopener');
        }, 500);
      }
    }
  };

  const handleSaveEdit = async (newText: string) => {
    if (text !== newText) {
      try {
        await dispatch(
          updateSegmentText({
            segmentId: segment.id,
            newText,
          })
        );
        setIsEditing(false);
        onEditEnd();
      } catch (error) {
        console.error('Failed to update segment text:', error);
      }
    } else {
      setIsEditing(false);
      onEditEnd();
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    onEditEnd();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(1),
        position: 'relative',
      }}
      onMouseEnter={() => {
        if (shouldBlur) {
          setShowUpgradeMessage(true);
        } else {
          setIsHovered(true);
        }
      }}
      onMouseLeave={() => {
        setShowUpgradeMessage(false);
        setIsHovered(false);
      }}
    >
      {shouldBlur && showUpgradeMessage && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
            width: '90%',
          }}
        >
          <Alert
            severity="info"
            icon={<Rocket />}
            action={
              <Button
                color="secondary"
                variant="outlined"
                size="small"
                onClick={handleUpgrade}
                sx={{
                  textTransform: 'none',
                  minWidth: '100px',
                }}
              >
                Upgrade
              </Button>
            }
            sx={{
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.shadows[4],
              '& .MuiAlert-message': {
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              },
            }}
          >
            <Typography variant="body2" color="text.primary">
              Your full transcript is ready! Upgrade now to view, copy, and
              export the complete transcript with speaker identification.
            </Typography>
          </Alert>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          columnGap: theme.spacing(1),
          marginBottom: theme.spacing(1),
        }}
      >
        <Box
          sx={{
            filter: shouldBlur ? 'blur(5px)' : 'none',
            pointerEvents: shouldBlur ? 'none' : 'auto',
          }}
        >
          <SpeakerSelect speaker={speaker} speakerId={speakerId || ''} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {segment.history && segment.history.length > 0 && (
            <>
              <Chip
                label="Edited"
                size="small"
                color="default"
                sx={{
                  height: 20,
                  backgroundColor: theme.palette.action.hover,
                  border: 'none',
                  color: 'text.secondary',
                  '& .MuiChip-label': {
                    fontSize: '0.75rem',
                  },
                }}
              />
              <Tooltip title="View edit history" placement="top">
                <IconButton
                  size="small"
                  onClick={() => setHistoryDialogOpen(true)}
                  sx={{
                    padding: 0.5,
                    color: 'primary.main',
                  }}
                >
                  <History fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: 'text.secondary',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                filter: shouldBlur ? 'blur(5px)' : 'none',
                pointerEvents: shouldBlur ? 'none' : 'auto',
                cursor: 'pointer',
              }}
              onClick={handleClick}
            >
              {formatTimestamp(formatSegmentTime(start))}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                filter: shouldBlur ? 'blur(5px)' : 'none',
                pointerEvents: shouldBlur ? 'none' : 'auto',
              }}
            >
              -
            </Typography>
            <Typography
              variant="caption"
              sx={{
                filter: shouldBlur ? 'blur(5px)' : 'none',
                pointerEvents: shouldBlur ? 'none' : 'auto',
                cursor: 'pointer',
              }}
              onClick={handleClick}
            >
              {formatTimestamp(formatSegmentTime(end))}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
        onMouseEnter={() => !shouldBlur && setIsHovered(true)}
        onMouseLeave={() => !shouldBlur && setIsHovered(false)}
      >
        {!shouldBlur && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              position: 'relative',
            }}
          >
            {isEditing ? (
              <EditDialog
                text={text}
                onSave={handleSaveEdit}
                onCancel={handleCancelEdit}
              />
            ) : (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{
                    filter: shouldBlur ? 'blur(5px)' : 'none',
                    cursor: shouldBlur ? 'default' : 'pointer',
                    pointerEvents: shouldBlur ? 'none' : 'auto',
                    backgroundColor: isHovered
                      ? theme.palette.background.default
                      : 'transparent',
                    transition: 'background-color 0.3s',
                    borderRadius: '8px',
                    padding: '0',
                    paddingRight: '40px',
                    flex: 1,
                    fontWeight: isCurrentSegment ? 'bold' : 'normal',
                  }}
                  onClick={handleClick}
                >
                  {shouldBlur ? (
                    `This transcript is available on a paid plan: ${scrambleText(
                      text
                    )}`
                  ) : (
                    <HighlightedText
                      text={text}
                      searchTerm={searchTerm || ''}
                    />
                  )}
                </Typography>

                <Box
                  sx={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    display: 'flex',
                    gap: '0.5rem',
                    zIndex: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      backgroundColor: theme.palette.background.paper,
                      borderRadius: '8px',
                      padding: '4px',
                      gap: '4px',
                    }}
                  >
                    {isHovered && !editingSegmentId && (
                      <>
                        <Tooltip title="Share clip" placement="bottom" arrow>
                          <IconButton
                            type="button"
                            aria-label="Share clip"
                            onClick={handleClipShare}
                            sx={{
                              padding: theme.spacing(0.5),
                              color: isClipCopied
                                ? 'success.main'
                                : 'secondary.main',
                              backgroundColor: 'transparent',
                              borderRadius: '8px',
                              height: '30px',
                              width: '30px',
                              '&:hover': {
                                backgroundColor: 'secondary.main',
                                color: 'common.white',
                              },
                            }}
                          >
                            {isClipCopied ? <Check /> : <Forward />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit segment" placement="bottom" arrow>
                          <IconButton
                            type="button"
                            aria-label="Edit"
                            onClick={handleEditClick}
                            sx={{
                              padding: theme.spacing(0.5),
                              color: 'secondary.main',
                              backgroundColor: 'transparent',
                              borderRadius: '8px',
                              height: '30px',
                              width: '30px',
                              '&:hover': {
                                backgroundColor: 'secondary.main',
                                color: 'common.white',
                              },
                            }}
                          >
                            <Pencil />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={isCopied ? 'Copied!' : 'Copy'}
                          placement="bottom"
                          arrow
                        >
                          <IconButton
                            type="button"
                            aria-label="Copy"
                            onClick={handleCopy}
                            disabled={isCopied}
                            sx={{
                              padding: theme.spacing(0.5),
                              color: 'secondary.main',
                              backgroundColor: 'transparent',
                              borderRadius: '8px',
                              height: '30px',
                              width: '30px',
                              '&:hover': {
                                backgroundColor: 'secondary.main',
                                color: 'common.white',
                              },
                            }}
                          >
                            {isCopied ? <Check /> : <Copy />}
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {((isHovered && !editingSegmentId) ||
                      hasNoteAtTimestamp()) &&
                      (hasNoteAtTimestamp() ? (
                        <IconButton
                          type="button"
                          aria-label="Comment"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAnchorEl(e.currentTarget);
                          }}
                          sx={{
                            padding: theme.spacing(0.5),
                            backgroundColor: 'transparent',
                            borderRadius: '8px',
                            height: '30px',
                            width: '30px',
                            '& .MuiSvgIcon-root': {
                              color: theme.palette.warning.main,
                            },
                            '&:hover': {
                              backgroundColor: theme.palette.warning.main,
                              '& .MuiSvgIcon-root': {
                                color: 'common.white',
                              },
                            },
                          }}
                        >
                          <MessageSquare />
                        </IconButton>
                      ) : (
                        <Tooltip title="Add note" placement="bottom" arrow>
                          <IconButton
                            type="button"
                            aria-label="Comment"
                            onClick={handleCommentClick}
                            sx={{
                              padding: theme.spacing(0.5),
                              color: 'secondary.main',
                              backgroundColor: 'transparent',
                              borderRadius: '8px',
                              height: '30px',
                              width: '30px',
                              '&:hover': {
                                backgroundColor: 'secondary.main',
                                color: 'common.white',
                              },
                            }}
                          >
                            <MessageSquare />
                          </IconButton>
                        </Tooltip>
                      ))}
                  </Box>
                </Box>
              </>
            )}
          </Box>
        )}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => {
            setAnchorEl(null);
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          sx={{
            pointerEvents: 'auto',
          }}
          PaperProps={{
            elevation: 1,
            sx: {
              mt: 0.5,
              overflow: 'hidden',
              minWidth: 280,
              maxWidth: 320,
              bgcolor: 'background.paper',
              borderRadius: 1,
            },
          }}
          disableEnforceFocus
          disableAutoFocus
          disableRestoreFocus
        >
          {getNotesInSegment().map((note, index) => (
            <MenuItem
              key={index}
              onClick={() => {
                setAnchorEl(null);
                setCommentDialogOpen(true);
                setSelectedNote(note);
              }}
              sx={{
                py: 1,
                px: 2,
                minHeight: 'auto',
                whiteSpace: 'normal',
              }}
            >
              <Box sx={{ width: '100%' }}>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    color: 'text.primary',
                    mb: 0.5,
                  }}
                >
                  {note.text}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    display: 'block',
                  }}
                >
                  {formatTimestamp(formatSegmentTime(note.timestamp))}
                </Typography>
              </Box>
            </MenuItem>
          ))}
          <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setSelectedNote(undefined);
                setCommentDialogOpen(true);
              }}
              sx={{
                py: 1,
                px: 2,
                color: 'text.secondary',
                '& .MuiSvgIcon-root': {
                  fontSize: 20,
                  mr: 1,
                },
              }}
            >
              <MessageSquare />
              <Typography variant="body2">Add comment</Typography>
            </MenuItem>
          </Box>
        </Popover>
        <CommentDialog
          open={commentDialogOpen}
          onClose={() => {
            setCommentDialogOpen(false);
            setSelectedNote(undefined);
          }}
          existingNote={selectedNote}
          timestamp={start}
        />
        <EditHistoryDialog
          open={historyDialogOpen}
          onClose={() => setHistoryDialogOpen(false)}
          segment={segment}
        />
      </Box>
    </Box>
  );
};

export default SegmentPanel;
